import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Sparkles, Users, Zap, TrendingUp, ArrowRight, Sun, Moon, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useTheme } from '../context/ThemeContext';
import { API_URL } from '../config/api';

const LOGOS = {
  dark: 'https://customer-assets.emergentagent.com/job_store-boost-1/artifacts/p00q8cnd_logo_darkversion.6219654a445e56ed3cd0.png',
  light: 'https://customer-assets.emergentagent.com/job_store-boost-1/artifacts/40mbqv8p_logo.a291411cbf7fc3847246.png'
};

const Success = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, toggleTheme } = useTheme();
  
  const sessionId = searchParams.get('session_id');
  
  const [currentStep, setCurrentStep] = useState(0);
  const [orderData, setOrderData] = useState(location.state || {});
  const [loading, setLoading] = useState(!!sessionId);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    // Dati Cliente
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    // Dati Attività
    business_name: '',
    business_type: '',
    business_address: '',
    business_city: '',
    business_cap: '',
    // Presenza Online
    website: '',
    instagram: '',
    facebook: '',
    google_maps: '',
    // Note
    notes: ''
  });

  const t = {
    bg: isDark ? 'bg-slate-950' : 'bg-slate-50',
    bgCard: isDark ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200 shadow-sm',
    bgCardAlt: isDark ? 'bg-slate-800/50' : 'bg-slate-100',
    text: isDark ? 'text-white' : 'text-slate-900',
    textSecondary: isDark ? 'text-slate-300' : 'text-slate-600',
    textMuted: isDark ? 'text-slate-400' : 'text-slate-500',
    border: isDark ? 'border-slate-800' : 'border-slate-200',
    input: isDark ? 'bg-slate-950 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400',
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    const verifySession = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
        const data = await response.json();

        if (response.ok) {
          if (data.payment_status === 'paid' || data.status === 'complete') {
            // Try to get customer_id from the session/subscription
            let stripeCustomerId = null;
            try {
              // First try with subscription_id if available from location state
              const subId = orderData.subscriptionId || sessionId;
              const detailsRes = await fetch(`${API_URL}/api/checkout-session-details?subscription_id=${subId}&session_id=${sessionId}`);
              if (detailsRes.ok) {
                const detailsData = await detailsRes.json();
                stripeCustomerId = detailsData.customer_id;
              }
            } catch (detailsErr) {
              console.log('Could not fetch customer details:', detailsErr);
            }
            
            setOrderData(prev => ({
              ...prev,
              sessionId,
              paymentStatus: data.payment_status,
              customerEmail: data.customer_email,
              stripeCustomerId: stripeCustomerId || prev.customerId
            }));
            if (data.customer_email) {
              setFormData(prev => ({ ...prev, customer_email: data.customer_email }));
            }
          } else {
            setError('Il pagamento non è stato completato. Riprova.');
          }
        } else {
          setError(data.error || 'Errore nella verifica del pagamento');
        }
      } catch (err) {
        console.error('Session verification error:', err);
        setError('Errore nella verifica del pagamento. Contatta il supporto.');
      }
      setLoading(false);
    };

    verifySession();
  }, [sessionId, orderData.subscriptionId]);

  // Pre-fill from orderData
  useEffect(() => {
    if (orderData.email) {
      setFormData(prev => ({ ...prev, customer_email: orderData.email }));
    }
    if (orderData.customerEmail) {
      setFormData(prev => ({ ...prev, customer_email: orderData.customerEmail }));
    }
  }, [orderData]);

  const steps = [
    { title: 'Conferma', icon: <Check className="w-5 h-5" /> },
    { title: 'Configura', icon: <Users className="w-5 h-5" /> },
    { title: 'Pronto!', icon: <Sparkles className="w-5 h-5" /> }
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Save configuration to Firebase with Stripe customer ID
      const response = await fetch(`${API_URL}/api/configurations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          subscription_id: orderData.subscriptionId || sessionId,
          stripe_customer_id: orderData.stripeCustomerId || orderData.customerId || null, // Link to Stripe customer
          plan_type: orderData.planType || orderData.plan
        })
      });

      if (!response.ok) {
        throw new Error('Errore nel salvataggio della configurazione');
      }

      setCurrentStep(2);
    } catch (err) {
      console.error('Submit error:', err);
      alert('Errore nel salvataggio. Il nostro team ti contatterà comunque!');
      setCurrentStep(2);
    }
    setSubmitting(false);
  };

  const getPlanName = () => {
    if (orderData.plan === 'premium') return 'Piano Gestionale Completo';
    return 'Piano Solo Visibilità';
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-slate-950' : 'bg-slate-50'}`}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className={t.textSecondary}>Verifica del pagamento in corso...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-slate-950' : 'bg-slate-50'}`}>
        <div className={`max-w-md w-full mx-4 p-8 rounded-2xl ${t.bgCard} border text-center`}>
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className={`text-xl font-bold mb-2 ${t.text}`}>Ops! Qualcosa è andato storto</h2>
          <p className={`mb-6 ${t.textSecondary}`}>{error}</p>
          <Button onClick={() => navigate('/checkout')} className="bg-emerald-500 hover:bg-emerald-600 text-white">
            Torna al Checkout
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col transition-colors ${isDark ? 'bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950' : 'bg-gradient-to-b from-slate-50 via-white to-slate-50'}`}>
      <div className="flex-1 flex items-center justify-center p-4 py-8">
        <div className="max-w-4xl w-full">
          {/* Logo */}
          <div className="text-center mb-6">
            <img src={isDark ? LOGOS.dark : LOGOS.light} alt="Skipres" className="h-8 md:h-10 mx-auto" />
          </div>

          {/* Success Animation */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-emerald-500/50 animate-bounce">
              <Check className="w-12 h-12 text-white" />
            </div>
            <h1 className={`text-3xl md:text-5xl font-bold mb-4 ${t.text}`}>
              🎉 Benvenuto in Skipres!
            </h1>
            <p className={`text-lg md:text-xl ${t.textSecondary}`}>
              Sei ufficialmente parte della famiglia Skipres
            </p>
          </div>

          {/* Order Summary */}
          <div className={`backdrop-blur-sm border rounded-2xl p-6 md:p-8 mb-8 ${t.bgCard}`}>
            <h2 className={`text-xl md:text-2xl font-bold mb-6 ${t.text}`}>Riepilogo Acquisto</h2>
            <div className="space-y-4">
              <div className={`flex justify-between items-center pb-4 border-b ${t.border}`}>
                <div>
                  <div className={`font-semibold ${t.text}`}>{getPlanName()}</div>
                  <div className={`text-sm ${t.textMuted}`}>
                    {orderData.planType === 'yearly' ? 'Annuale' : 'Mensile'}
                  </div>
                </div>
                <div className="text-emerald-500 font-bold">
                  {orderData.total ? `€${orderData.total.toFixed(2)}` : '✓ Pagato'}
                </div>
              </div>
              
              {orderData.elit && (
                <div className={`flex justify-between items-center pb-4 border-b ${t.border}`}>
                  <div>
                    <div className={`font-semibold flex items-center gap-2 ${t.text}`}>
                      Programma Elit
                      <Badge className="bg-emerald-500 text-white text-xs">Attivo</Badge>
                    </div>
                    <div className={`text-sm ${t.textMuted}`}>20% solo su vendite generate</div>
                  </div>
                  <div className="text-emerald-500 font-bold">Incluso</div>
                </div>
              )}

              {(orderData.customerEmail || orderData.email) && (
                <div className={`text-sm ${t.textMuted}`}>
                  Ricevuta inviata a: <span className={t.text}>{orderData.customerEmail || orderData.email}</span>
                </div>
              )}
            </div>
          </div>

          {/* Steps Progress */}
          <div className={`backdrop-blur-sm border rounded-2xl p-6 md:p-8 mb-8 ${t.bgCard}`}>
            <div className="flex justify-between items-center mb-8">
              {steps.map((step, index) => (
                <React.Fragment key={index}>
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                      index <= currentStep
                        ? 'bg-emerald-500 text-white'
                        : isDark ? 'bg-slate-800 text-slate-500' : 'bg-slate-200 text-slate-400'
                    }`}>
                      {step.icon}
                    </div>
                    <div className={`text-xs md:text-sm font-medium text-center ${
                      index <= currentStep ? t.text : t.textMuted
                    }`}>
                      {step.title}
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 md:mx-4 rounded-full transition-all ${
                      index < currentStep ? 'bg-emerald-500' : isDark ? 'bg-slate-800' : 'bg-slate-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {currentStep === 0 && (
              <div className="text-center">
                <p className={`mb-6 ${t.textSecondary}`}>
                  Il tuo pagamento è stato confermato! Ora configura la tua attività.
                </p>
                <Button
                  onClick={() => setCurrentStep(1)}
                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-8 py-4"
                  data-testid="configure-business-btn"
                >
                  Configura Ora La Tua Attività
                </Button>
              </div>
            )}

            {currentStep === 1 && (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Dati Cliente */}
                <div>
                  <h3 className={`text-lg font-semibold mb-4 ${t.text}`}>👤 I Tuoi Dati</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>Nome Completo *</label>
                      <input type="text" name="customer_name" required value={formData.customer_name} onChange={handleInputChange} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} placeholder="Mario Rossi" />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>Email *</label>
                      <input type="email" name="customer_email" required value={formData.customer_email} onChange={handleInputChange} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} placeholder="info@..." />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>Telefono *</label>
                      <input type="tel" name="customer_phone" required value={formData.customer_phone} onChange={handleInputChange} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} placeholder="+39 ..." />
                    </div>
                  </div>
                </div>

                {/* Dati Attività */}
                <div>
                  <h3 className={`text-lg font-semibold mb-4 ${t.text}`}>🏪 La Tua Attività</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>Nome Attività *</label>
                      <input type="text" name="business_name" required value={formData.business_name} onChange={handleInputChange} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} placeholder="Salone Bellezza Milano" />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>Tipo di Attività *</label>
                      <select name="business_type" required value={formData.business_type} onChange={handleInputChange} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`}>
                        <option value="">Seleziona...</option>
                        <option value="parrucchiere">Parrucchiere</option>
                        <option value="barbiere">Barbiere</option>
                        <option value="estetica">Centro Estetico</option>
                        <option value="nails">Centro Nails</option>
                        <option value="spa">Spa / Wellness</option>
                        <option value="palestra">Palestra / Fitness</option>
                        <option value="ristorante">Ristorante</option>
                        <option value="negozio">Negozio / Shop</option>
                        <option value="studio">Studio Professionale</option>
                        <option value="altro">Altro</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>Indirizzo *</label>
                      <input type="text" name="business_address" required value={formData.business_address} onChange={handleInputChange} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} placeholder="Via Roma, 123" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>Città *</label>
                        <input type="text" name="business_city" required value={formData.business_city} onChange={handleInputChange} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} placeholder="Milano" />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>CAP</label>
                        <input type="text" name="business_cap" value={formData.business_cap} onChange={handleInputChange} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} placeholder="20100" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Presenza Online */}
                <div>
                  <h3 className={`text-lg font-semibold mb-2 ${t.text}`}>🌐 Presenza Online</h3>
                  <p className={`text-sm mb-4 ${t.textMuted}`}>Opzionale - ci aiuta a creare una vetrina migliore</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>Sito Web</label>
                      <input type="url" name="website" value={formData.website} onChange={handleInputChange} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} placeholder="https://www.tuosito.it" />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>Instagram</label>
                      <input type="text" name="instagram" value={formData.instagram} onChange={handleInputChange} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} placeholder="@tuoaccount" />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>Facebook</label>
                      <input type="text" name="facebook" value={formData.facebook} onChange={handleInputChange} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} placeholder="facebook.com/tuapagina" />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>Link Google Maps</label>
                      <input type="url" name="google_maps" value={formData.google_maps} onChange={handleInputChange} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${t.input}`} placeholder="https://maps.google.com/..." />
                    </div>
                  </div>
                </div>

                {/* Note */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${t.textSecondary}`}>Note Aggiuntive</label>
                  <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={3} className={`w-full border rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none ${t.input}`} placeholder="Informazioni aggiuntive che possono aiutarci a configurare al meglio la tua vetrina..." />
                </div>

                {/* Nota */}
                <div className={`rounded-lg p-4 ${isDark ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-100'}`}>
                  <p className={`text-sm ${t.textSecondary}`}>
                    <span className="text-emerald-500 font-semibold">📞 Questa è la configurazione iniziale.</span> Il nostro team ti contatterà in pochi minuti per confermare i dati e completare insieme la configurazione della tua vetrina. Ci occupiamo noi di tutto!
                  </p>
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white py-4 text-lg disabled:opacity-50" data-testid="complete-config-btn">
                  {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Salvataggio...</> : 'Completa Configurazione'}
                </Button>
              </form>
            )}

            {currentStep === 2 && (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className={`text-2xl font-bold mb-2 ${t.text}`}>🎉 Configurazione Inviata!</h3>
                  <p className={t.textSecondary}>
                    La tua attività <span className="text-emerald-500 font-semibold">{formData.business_name}</span> è in fase di configurazione
                  </p>
                </div>

                <div className={`rounded-xl p-6 ${isDark ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
                  <p className={`font-semibold mb-2 ${t.text}`}>⏰ Cosa succede ora?</p>
                  <ul className={`text-left space-y-2 ${t.textSecondary}`}>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Il nostro team riceve la tua configurazione</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Ti contattiamo entro 24h per confermare i dettagli</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Creiamo la tua vetrina personalizzata</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Sarai visibile su 50.000+ utenti!</span>
                    </li>
                  </ul>
                </div>

                {orderData.elit && (
                  <div className={`rounded-xl p-6 ${isDark ? 'bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30' : 'bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200'}`}>
                    <Badge className="mb-3 bg-emerald-500 text-white">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Programma Elit Attivo
                    </Badge>
                    <p className={`mb-2 ${t.text}`}>
                      Il nostro team marketing inizierà le campagne pubblicitarie per la tua attività entro 48h!
                    </p>
                    <p className={`text-sm ${t.textMuted}`}>
                      Riceverai un&apos;email con tutti i dettagli delle campagne attive
                    </p>
                  </div>
                )}

                <div className={`p-4 rounded-xl ${isDark ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'}`}>
                  <p className={`text-sm ${t.textSecondary}`}>
                    <span className="text-blue-500 font-semibold">📧 Controlla la tua email!</span> Ti abbiamo inviato una conferma a <span className={t.text}>{formData.customer_email || orderData.email}</span>
                  </p>
                </div>

                <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-8 py-4 group" data-testid="go-home-btn">
                  Torna alla Home
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            )}
          </div>

          {/* Tips */}
          {currentStep < 2 && (
            <div className="grid md:grid-cols-3 gap-4 md:gap-6">
              {[
                { icon: <Zap className="w-6 h-6" />, title: 'Setup Veloce', description: 'Completa la configurazione in meno di 5 minuti' },
                { icon: <Users className="w-6 h-6" />, title: 'Supporto Dedicato', description: 'Il nostro team ti contatterà entro 24h' },
                { icon: <TrendingUp className="w-6 h-6" />, title: 'Inizia a Vendere', description: 'Sarai visibile su 50.000+ utenti' }
              ].map((tip, index) => (
                <div key={index} className={`backdrop-blur-sm border rounded-xl p-5 md:p-6 ${t.bgCard}`}>
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-500 mb-3">{tip.icon}</div>
                  <h3 className={`font-semibold mb-2 ${t.text}`}>{tip.title}</h3>
                  <p className={`text-sm ${t.textMuted}`}>{tip.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className={`py-4 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="container mx-auto px-4 flex items-center gap-3">
          <button onClick={toggleTheme} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] md:text-xs border transition-all ${t.bgCard}`} data-testid="theme-toggle-btn">
            {isDark ? <><Sun className="w-3 h-3" /><span>Light</span></> : <><Moon className="w-3 h-3" /><span>Dark</span></>}
          </button>
          <div className={`text-[10px] md:text-xs ${t.textMuted}`}>© 2026 Skipres</div>
        </div>
      </footer>
    </div>
  );
};

export default Success;
